import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Box, Paper, Typography, IconButton, Tooltip,
  CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button, TextField, Grid
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import SmartToyIcon from "@mui/icons-material/SmartToy";

const MAX_REPLY_DELAY_MS = 86399000;
const replyDelayToClock = (value) => {
  const totalSeconds = Math.floor(Math.min(Math.max(Number(value) || 0, 0), MAX_REPLY_DELAY_MS) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds].map(part => String(part).padStart(2, "0")).join(":");
};
const clockToReplyDelay = (value) => {
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || "");
  if (!match) return 0;
  const hours = Math.min(Number(match[1]), 23);
  const minutes = Math.min(Number(match[2]), 59);
  const seconds = Math.min(Number(match[3] || 0), 59);
  return ((hours * 60 + minutes) * 60 + seconds) * 1000;
};

const MAX_BLOCK_GAP_MS = 15000;
const blockGapToClock = (value) => {
  const totalSeconds = Math.round(Math.min(Math.max(Number(value) || 0, 0), MAX_BLOCK_GAP_MS) / 1000);
  return `00:00:${String(totalSeconds).padStart(2, "0")}`;
};
const clockToBlockGap = (value) => {
  const match = /^(\d{2}):(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) return 0;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  return Math.min(Math.max(((hours * 60 + minutes) * 60 + seconds) * 1000, 0), MAX_BLOCK_GAP_MS);
};


export default function AiAgents() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [editAgent, setEditAgent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-agents");
      setAgents(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    try {
      await api.delete(`/ai-agents/${deleteId}`);
      setAgents(a => a.filter(x => x.id !== deleteId));
      setDeleteId(null);
    } catch (e) { console.error(e); }
  };

  const handleEditOpen = (agent) => {
    setEditAgent({ ...agent,
      replyDelayMinMs: agent.replyDelayMinMs ?? 3000,
      replyDelayMaxMs: agent.replyDelayMaxMs ?? 3000,
      blockGapMinMs: agent.blockGapMinMs ?? 1200,
      blockGapMaxMs: agent.blockGapMaxMs ?? 2400,
      continueKeywords: (() => {
        try { return JSON.parse(agent.continueKeywords || "[]"); } catch { return []; }
      })()
    });
  };

  const handleEditSave = async () => {
    if (!editAgent) return;
    setSaving(true);
    try {
      const payload = { ...editAgent };
      if (Array.isArray(payload.continueKeywords)) {
        payload.continueKeywords = JSON.stringify(payload.continueKeywords);
      }
      const { data } = await api.put(`/ai-agents/${editAgent.id}`, payload);
      setAgents(a => a.map(x => x.id === data.id ? data : x));
      setEditAgent(null);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <Box style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
      <Box style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <SmartToyIcon style={{ color: "#01BBAC", fontSize: 28 }} />
        <Typography variant="h5" style={{ fontWeight: 700, color: "#333" }}>Agentes de IA</Typography>
      </Box>

      <Box style={{ padding: "10px 16px", background: "rgba(1,187,172,0.07)", border: "1px solid rgba(1,187,172,0.3)", borderRadius: 8, marginBottom: 20, fontSize: 13, color: "#555" }}>
        Aqui irão aparecer todos os agentes de IA que você criar dentro do <strong>Fluxo de Conversa</strong>.
        Ao editar um agente aqui, as alterações são aplicadas automaticamente em todos os fluxos onde ele está sendo usado.
      </Box>

      {loading ? (
        <Box style={{ display: "flex", justifyContent: "center", padding: 40 }}><CircularProgress style={{ color: "#01BBAC" }} /></Box>
      ) : agents.length === 0 ? (
        <Box style={{ textAlign: "center", padding: 60, color: "#aaa" }}>
          <SmartToyIcon style={{ fontSize: 64, color: "#ddd", marginBottom: 12 }} />
          <Typography style={{ fontSize: 16, fontWeight: 600 }}>Nenhum agente salvo ainda</Typography>
          <Typography style={{ fontSize: 13, marginTop: 6 }}>Abra um Agente OpenAI no Fluxo de Conversa, configure e clique em "Salvar Agente".</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {agents.map(agent => (
            <Grid item xs={12} sm={6} key={agent.id}>
              <Paper style={{ padding: 16, borderRadius: 10, border: "1px solid #eee", height: "100%" }}>
                <Box style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                    <SmartToyIcon style={{ color: "#01BBAC", fontSize: 20 }} />
                    <Typography style={{ fontWeight: 700, fontSize: 14, color: "#222" }}>{agent.name}</Typography>
                  </Box>
                  <Box style={{ display: "flex", gap: 4 }}>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleEditOpen(agent)}><EditIcon fontSize="small" style={{ color: "#01BBAC" }} /></IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => setDeleteId(agent.id)}><DeleteIcon fontSize="small" style={{ color: "#e57373" }} /></IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Box style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, background: "#f3e5f5", color: "#6a1b9a", padding: "2px 8px", borderRadius: 10 }}>temp: {agent.temperature}</span>
                  <span style={{ fontSize: 11, background: "#e8f5e9", color: "#2e7d32", padding: "2px 8px", borderRadius: 10 }}>{agent.maxTokens} tokens</span>
                </Box>
                {agent.prompt && (
                  <Typography style={{ fontSize: 11, color: "#777", marginTop: 8, lineHeight: 1.5,
                    display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {agent.prompt}
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Excluir agente</DialogTitle>
        <DialogContent>
          <DialogContentText>Tem certeza que deseja excluir este agente? Os fluxos que o utilizam não serão afetados.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAgent} onClose={() => setEditAgent(null)} fullWidth maxWidth="md">
        <DialogTitle style={{ borderBottom: "2px solid #01BBAC" }}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EditIcon style={{ color: "#01BBAC" }} />
            <span>Editar Agente — {editAgent?.name}</span>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 20 }}>
          {editAgent && (
            <Box style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Box style={{ padding: "8px 12px", background: "rgba(1,187,172,0.07)", borderRadius: 6, fontSize: 12, color: "#555" }}>
                As alterações salvas aqui serão aplicadas automaticamente em todos os fluxos onde este agente está sendo usado.
              </Box>
              <TextField label="Nome do agente" value={editAgent.name || ""}
                onChange={e => setEditAgent(a => ({ ...a, name: e.target.value }))}
                variant="outlined" size="small" fullWidth />
              <TextField label="Prompt (treinamento)" value={editAgent.prompt || ""}
                onChange={e => setEditAgent(a => ({ ...a, prompt: e.target.value }))}
                variant="outlined" multiline rows={8} fullWidth
                placeholder="Descreva como a IA deve se comportar..." />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Temperatura" type="number" size="small"
                    inputProps={{ min: 0, max: 1, step: 0.1 }}
                    value={editAgent.temperature ?? 0.3}
                    onChange={e => setEditAgent(a => ({ ...a, temperature: parseFloat(e.target.value) }))}
                    variant="outlined" fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Max Tokens" type="number" size="small"
                    value={editAgent.maxTokens || 4000}
                    onChange={e => setEditAgent(a => ({ ...a, maxTokens: parseInt(e.target.value) }))}
                    variant="outlined" fullWidth />
                </Grid>
              </Grid>
              <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                Tempo após a última mensagem do cliente (HH:MM:SS)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Espera mínima" type="time" size="small"
                    inputProps={{ min: "00:00:00", max: "23:59:59", step: 1 }}
                    value={replyDelayToClock(editAgent.replyDelayMinMs ?? 3000)}
                    onChange={e => setEditAgent(a => ({ ...a, replyDelayMinMs: clockToReplyDelay(e.target.value) }))}
                    helperText="Igual ao máximo = tempo fixo"
                    variant="outlined" fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Espera máxima" type="time" size="small"
                    inputProps={{ min: "00:00:00", max: "23:59:59", step: 1 }}
                    value={replyDelayToClock(editAgent.replyDelayMaxMs ?? 3000)}
                    onChange={e => setEditAgent(a => ({ ...a, replyDelayMaxMs: clockToReplyDelay(e.target.value) }))}
                    helperText="Diferente = sorteio sem repetir o último"
                    variant="outlined" fullWidth />
                </Grid>
              </Grid>
              <Typography variant="subtitle2" style={{ marginTop: 8 }}>
                Intervalo entre blocos da IA (HH:MM:SS)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Intervalo mínimo" type="time" size="small"
                    inputProps={{ min: "00:00:00", max: "00:00:15", step: 1 }}
                    value={blockGapToClock(editAgent.blockGapMinMs ?? 1200)}
                    onChange={e => setEditAgent(a => ({ ...a, blockGapMinMs: clockToBlockGap(e.target.value) }))}
                    variant="outlined" fullWidth />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="Intervalo máximo" type="time" size="small"
                    inputProps={{ min: "00:00:00", max: "00:00:15", step: 1 }}
                    value={blockGapToClock(editAgent.blockGapMaxMs ?? 2400)}
                    onChange={e => setEditAgent(a => ({ ...a, blockGapMaxMs: clockToBlockGap(e.target.value) }))}
                    variant="outlined" fullWidth />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px", borderTop: "1px solid #eee" }}>
          <Button onClick={() => setEditAgent(null)} variant="outlined">Cancelar</Button>
          <Button onClick={handleEditSave} variant="contained" disabled={saving}
            style={{ background: "#01BBAC", color: "#fff", fontWeight: 700 }}>
            {saving ? "Salvando..." : "Salvar Alterações"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
